import React, { useState, useEffect, useCallback } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/outline'

export interface GoalStats {
  title: string
  subtitle: string
  value: number
  clientCount?: number
}

interface IAppGoalStatsCardProps {
  stats: GoalStats[]
}

export const AppGoalStatsCard: React.FC<IAppGoalStatsCardProps> = ({ stats }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === stats.length - 1 ? 0 : prev + 1))
  }, [stats.length])

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? stats.length - 1 : prev - 1))
  }

  // Auto-slide effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout

    if (!isPaused) {
      intervalId = setInterval(() => {
        nextSlide()
      }, 5000) // Change slide every 5 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPaused, nextSlide])

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Carousel dots */}
      <div className="absolute right-6 top-6 flex gap-2 z-10">
        {stats.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              currentSlide === index ? 'bg-white w-4' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Card content */}
      <div className="relative overflow-hidden">
        <div
          className={`transition-transform duration-500 ease-in-out`}
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
          }}
        >
          <div className="flex">
            {stats.map((stat, index) => (
              <div key={index} className={`flex-none w-full`}>
                <div
                  className={`h-[335px] sm:flex sm:h-60 mdl:h-48 w-full sm:gap-7 p-6 overflow-hidden rounded-2xl bg-gradient-to-r from-app-primary-110 to-app-primary-120 border border-app-primary-55 shadow-[0px_16px_32px_-16px_#00000014]`}
                >
                  <div className="flex flex-col gap-2 sm:flex-[1] xl:gap-3">
                    <h4 className="text-white font-inter text-3xl lg:text-4xl leading-10 font-bold self-stretch">
                      {stat.title}
                    </h4>
                    {stat.clientCount !== undefined && (
                      <div className="inline-flex font-inter px-4 py-3 bg-app-primary-70 rounded-lg w-fit">
                        <span className="text-white text-sm font-medium">
                          {stat.clientCount} Clients
                        </span>
                      </div>
                    )}
                    <p className="text-white text-sm font-normal font-inter">{stat.subtitle}</p>
                  </div>
                  <div className="relative sm:flex-[1] flex justify-center items-center mt-32 sm:mt-0">
                    <div
                      className={`absolute -bottom-12 shadow-white shadow-[0px_2px_45px_0px_rgba(255,255,255,1)] px-5 py-9 w-[165px] h-[165px] flex flex-col items-center justify-center border border-white rounded-4xl bg-gradient-to-r from-app-primary-95 to-app-primary-75 rotate-6`}
                    >
                      <span className="text-white font-bold font-inter text-[90px]">
                        {stat.value}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      <div className="absolute bottom-6 right-6 flex gap-2">
        <button
          onClick={() => {
            prevSlide()
            setIsPaused(true) // Pause auto-slide when manually navigating
            setTimeout(() => setIsPaused(false), 3000) // Resume after 3 seconds
          }}
          className="p-2 rounded-lg border border-white/50 bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" stroke="white" strokeOpacity={0.5} />
        </button>
        <button
          onClick={() => {
            nextSlide()
            setIsPaused(true) // Pause auto-slide when manually navigating
            setTimeout(() => setIsPaused(false), 3000) // Resume after 3 seconds
          }}
          className="p-2 rounded-lg border border-white/50 bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ArrowRightIcon className="w-5 h-5" stroke="white" strokeOpacity={0.5} />
        </button>
      </div>
    </div>
  )
}
